import { useState } from "react";
import { TreeItem, SimpleTreeView } from "@mui/x-tree-view-pro";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import ProgramDayTreeItemLabel from "./ProgramDayTreeItemLabel";
import ProgramWeekTreeItemLabel from "./ProgramWeekTreeItemLabel";
import SwitchProgramWeekForm from "./SwitchProgramWeekForm";
import SwitchProgramDayForm from "./SwitchProgramDayForm";
import ProgramWeekForm from "../ProgramWeekForm";
import ProgramDayForm from "../ProgramDayForm";
import { programDayTitles } from "../../actprep-ui/constants/program";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { useConfirm } from "material-ui-confirm";

export function generateProgramWeekNavItems(programWeeks) {
  const getProgramDayFromOrder = (programWeek, order) => {
    return programWeek.program_days.find(
      (programDay) => programDay.order === order
    );
  };

  return programWeeks.map((week, i) => ({
    id: `${i}_${week.uuid}`,
    label: week.title,
    data: week,
    type: "ProgramWeek",
    children: programDayTitles.map((dayTitle, j) => {
      const programDay = getProgramDayFromOrder(week, j);
      return {
        id: `${i}_${week.uuid}__${dayTitle}`,
        label: `${dayTitle}: ${programDay?.title || "None"}`,
        data: programDay,
        type: "ProgramDay",
      };
    }),
  }));
}

export default function ProgramWeekNavTree({
  nested,
  program,
  navItems,
  setAction,
  setProgramWeek,
  onProgramDaySelect,
}) {
  const [switchWeekFormOpen, setSwitchWeekFormOpen] = useState(false);
  const [switchDayFormOpen, setSwitchDayFormOpen] = useState(false);
  const [editWeekFormOpen, setEditWeekFormOpen] = useState(false);
  const [editDayFormOpen, setEditDayFormOpen] = useState(false);
  const [switchingWeekInfo, setSwitchingWeekInfo] = useState({
    currentProgramWeek: null,
    order: null,
  });
  const [switchingDayInfo, setSwitchingDayInfo] = useState({
    programWeek: null,
    currentProgramDay: null,
    order: null,
  });
  const [focusedProgramWeek, setFocusedProgramWeek] = useState(null);
  const [editingProgramDay, setEditingProgramDay] = useState(null);
  const confirm = useConfirm();

  const handleDuplicateWeek = async (focusedProgramWeek) => {
    if (!focusedProgramWeek) {
      return;
    }

    let response = await api.post(apiUrls.admin.programWeeks(), {
      duplicate: true,
      existing_program_week_uuid: focusedProgramWeek?.uuid,
    });
    let { data } = response;
    if (data?.success) {
      showSuccessSnackbar("Program Week Duplicated!");
    } else {
      return;
    }

    if (!program) {
      if (setProgramWeek) {
        setProgramWeek(data.program_week);
      }
      return;
    }

    response = await api.put(apiUrls.admin.programWeekToProgram(), {
      program_uuid: program.uuid,
      new_program_week_uuid: data.program_week.uuid,
      order: focusedProgramWeek.order,
    });
    if (response.data?.success && setAction) {
      setAction("reload");
    }
  };

  const handleDuplicateWeekClick = (event, programWeek, order) => {
    confirmFunc(
      `Are you sure you want to create a copy of '${programWeek?.title}' Program Week?`,
      () =>
        handleDuplicateWeek({
          ...programWeek,
          order,
        })
    );
  };

  const handleEditWeekClick = (event, programWeek) => {
    event.stopPropagation();
    setFocusedProgramWeek(programWeek);
    setEditWeekFormOpen(true);
  };

  const handleEditDayClick = (event, programDay) => {
    event.stopPropagation();
    setEditingProgramDay(programDay);
    setEditDayFormOpen(true);
  };

  const handleNavTreeUpdated = () => {
    if (setAction) {
      setAction("reload");
    }
  };

  const confirmFunc = (description, callback) => {
    confirm({
      description: description,
    }).then(() => {
      callback();
    });
  };

  const handleRemoveWeek = async (removeProgramWeekInfo) => {
    if (!removeProgramWeekInfo) {
      return;
    }
    const payload = {
      program_uuid: removeProgramWeekInfo.program.uuid,
      order: removeProgramWeekInfo.order,
    };
    const response = await api.delete(apiUrls.admin.programWeekToProgram(), {
      data: payload,
    });
    if (response.data?.success) {
      handleNavTreeUpdated();
    }
  };

  const handleRemoveDay = async (removeProgramDayInfo) => {
    if (!removeProgramDayInfo) {
      return;
    }
    const payload = {
      program_week_uuid: removeProgramDayInfo.programWeek.uuid,
      order: removeProgramDayInfo.order,
    };
    const response = await api.delete(apiUrls.admin.programDayToProgramWeek(), {
      data: payload,
    });
    if (response.data?.success) {
      handleNavTreeUpdated();
    }
  };

  const handleRemoveWeekClick = (event, programWeek, order) => {
    event.stopPropagation();
    if (!program) {
      return;
    }
    confirmFunc(
      `Please confirm that you want to remove ${programWeek.title} from ${program?.title}`,
      () =>
        handleRemoveWeek({
          program: program,
          order: order,
        })
    );
  };

  const handleRemoveDayClick = (event, programWeek, programDay, order) => {
    event.stopPropagation();
    if (!programDay) {
      return;
    }

    confirmFunc(
      `Please confirm that you want to remove ${programDay?.title} from ${programWeek.title}`,
      () =>
        handleRemoveDay({
          programWeek: programWeek,
          order: order,
        })
    );
  };
  const handleSwitchWeekClick = (event, programWeek, order) => {
    event.stopPropagation();
    setSwitchingWeekInfo({
      currentProgramWeek: programWeek,
      order: order,
    });
    setSwitchWeekFormOpen(true);
  };

  const handleSwitchDayClick = (event, programWeek, programDay, order) => {
    event.stopPropagation();
    setSwitchingDayInfo({
      programWeek: programWeek,
      currentProgramDay: programDay,
      order: order,
    });
    setSwitchDayFormOpen(true);
  };

  const handleProgramDayClick = (event, programDay) => {
    event.stopPropagation();
    if (!onProgramDaySelect) {
      return;
    }

    if (!programDay) {
      onProgramDaySelect(event, null);
    }
    onProgramDaySelect(event, programDay);
  };

  const Tree = navItems.map((programWeek, i) => (
    <TreeItem
      key={programWeek.id}
      itemId={programWeek.id}
      label={
        <ProgramWeekTreeItemLabel
          programWeek={programWeek.data}
          order={i}
          handleDuplicateWeekClick={handleDuplicateWeekClick}
          handleEditWeekClick={handleEditWeekClick}
          handleSwitchWeekClick={program ? handleSwitchWeekClick : null}
          handleRemoveWeekClick={program ? handleRemoveWeekClick : null}
        />
      }
    >
      {!programWeek.data.is_break_week &&
        programWeek.children.map((programDayNavItem, j) => (
          <TreeItem
            onClick={(event) =>
              handleProgramDayClick(event, programDayNavItem.data)
            }
            itemId={programDayNavItem.id}
            key={programDayNavItem.id}
            label={
              <ProgramDayTreeItemLabel
                programWeek={programWeek.data}
                programDayNavItem={programDayNavItem}
                order={j}
                handleRemoveDayClick={handleRemoveDayClick}
                handleSwitchDayClick={handleSwitchDayClick}
                handleEditDayClick={handleEditDayClick}
              />
            }
          />
        ))}
    </TreeItem>
  ));
  return (
    <>
      {nested ? Tree : <SimpleTreeView>{Tree}</SimpleTreeView>}
      {switchWeekFormOpen && program && (
        <SwitchProgramWeekForm
          open={switchWeekFormOpen}
          setOpen={setSwitchWeekFormOpen}
          program={program}
          onProgramWeekSwitched={handleNavTreeUpdated}
          {...switchingWeekInfo}
        />
      )}
      {switchDayFormOpen && (
        <SwitchProgramDayForm
          open={switchDayFormOpen}
          setOpen={setSwitchDayFormOpen}
          onProgramDaySwitched={handleNavTreeUpdated}
          {...switchingDayInfo}
        />
      )}
      {editWeekFormOpen && (
        <ProgramWeekForm
          open={editWeekFormOpen}
          setOpen={setEditWeekFormOpen}
          initialValue={focusedProgramWeek?.title}
          initialWeek={focusedProgramWeek}
          setValue={() => handleNavTreeUpdated()}
        />
      )}
      {editDayFormOpen && (
        <ProgramDayForm
          open={editDayFormOpen}
          setOpen={setEditDayFormOpen}
          initialDay={editingProgramDay}
          setValue={() => handleNavTreeUpdated()}
        />
      )}
    </>
  );
}
